/**
 * TimeCraft Input Service
 * Handles all API calls for TimeCraft Input management
 */

import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

const BASE_URL = Config.nodeSiteUrl + "v1/academic/timecraft-input";
const BOARD_BASE_URL = Config.nodeSiteUrl + "v1/academic/timecraft-board";
const BOARD_RESOURCE_BASE_URL = Config.nodeSiteUrl + "v1/academic/timecraft-board-resource";

export interface InstructorAssignment {
  instructorId: number;
  code: string;
  name: string;
  role: 'primary' | 'secondary';
  remarks?: string | null;
}

export interface SchedulingConfig {
  sessionDurationMinutes?: number;
  minDaysBetween?: number;
  maxPerDay?: number;
  maxPerWeek?: number;
  preferredDays?: string[];
}

export interface TimecraftInput {
  id: number;
  boardId?: number | null;
  shiftId?: number | null;
  moduleId?: number | null;
  moduleCode?: string | null;
  moduleName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  enrollments?: number | null;
  plannedCapacity?: number | null;
  sessions?: number | null;
  studentGroup?: string | null;
  instructorId?: number | null;
  instructorName?: string | null;
  instructorCode?: string | null;
  instructors?: InstructorAssignment[] | null;
  lessonPlanId?: number | null;
  lessonPlanName?: string | null;
  shiftName?: string | null;
  cypShiftId?: number | null;
  cypShiftName?: string | null;
  schedulingConfig?: SchedulingConfig | null;
  status?: 'Open' | 'Processed' | 'Rejected' | null;
  board?: {
    id: number;
    name: string;
    code: string;
  } | null;
}

export interface TimecraftBoard {
  id: number;
  name: string;
  code: string;
  authorisedUserIds?: string | null;
  remarks?: string | null;
  createdDate?: string;
  createdBy?: number;
  inputCount?: number;
}

export interface ListInputsParams {
  boardId?: number | string;
  status?: string;
  programId?: number | string;
  moduleCode?: string;
  instructorId?: number;
  instructorName?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface CreateInputData {
  boardId?: number | null;
  shiftId?: number | null;
  moduleId?: number;
  moduleCode: string;
  startDate?: string;
  endDate?: string;
  enrollments?: number;
  plannedCapacity?: number;
  sessions?: number;
  studentGroup?: string;
  instructorId?: number;
  instructorName?: string;
  instructorCode?: string;
  status?: 'Open' | 'Processed' | 'Rejected';
  schedulingConfig?: SchedulingConfig | null;
}

export interface UpdateInputData extends Partial<CreateInputData> {}

export interface BulkAssignBoardData {
  inputIds: number[];
  boardId: number | null;
}

export interface BulkUpdateStatusData {
  inputIds: number[];
  status: 'Open' | 'Processed' | 'Rejected';
}

export interface BulkDeleteInputsData {
  inputIds: number[];
  includeProcessed?: boolean;
  includeOpen?: boolean;
  includeRejected?: boolean;
  includeNull?: boolean;
}

export interface CreateBoardData {
  name: string;
  code: string;
  authorisedUserIds?: number[];
  remarks?: string;
}

export interface Classroom {
  id: number;
  name: string | null;
  capacity: number | null;
  centreId: number;
  isActive: number;
  remarks?: string | null;
}

export interface Shift {
  id: number;
  name: string;
  code: string | null;
  startTime: string | null;
  endTime: string | null;
}

export interface BoardResource {
  id: number;
  boardId: number;
  classroomId: number;
  shiftId: number;
  isActive: number;
  createdDate: string;
  createdBy: number;
  classroom: Classroom | null;
  shift: Shift | null;
}

export interface AddBoardResourceData {
  boardId: number;
  classroomId: number;
  shiftId: number;
  createdBy?: number;
}

export interface BulkAddBoardResourcesData {
  boardId: number;
  resources: { classroomId: number; shiftId: number }[];
  createdBy?: number;
}

export interface ResourceSummary {
  totalResources: number;
  totalCapacity: number;
  uniqueClassrooms: number;
  uniqueShifts: number;
  shiftBreakdown: {
    shiftId: number;
    shiftName: string;
    shiftCode: string | null;
    resourceCount: number;
  }[];
}

class TimeCraftInputService {
  // ============ Input Management ============

  /**
   * List all timecraft inputs with filters
   */
  listInputs(params: ListInputsParams = {}) {
    const queryString = new URLSearchParams(params as any).toString();
    return myAxios.get(`${BASE_URL}${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get a single input by ID
   */
  getInput(id: number) {
    return myAxios.get(`${BASE_URL}/${id}`);
  }

  /**
   * Create a new input
   */
  createInput(data: CreateInputData) {
    return myAxios.post(BASE_URL, data);
  }

  /**
   * Update an existing input
   */
  updateInput(id: number, data: UpdateInputData) {
    return myAxios.put(`${BASE_URL}/${id}`, data);
  }

  /**
   * Delete an input
   */
  deleteInput(id: number) {
    return myAxios.delete(`${BASE_URL}/${id}`);
  }

  /**
   * Bulk assign inputs to a board
   */
  bulkAssignBoard(data: BulkAssignBoardData) {
    return myAxios.post(`${BASE_URL}/bulk-assign-board`, data);
  }

  /**
   * Bulk update status of inputs
   */
  bulkUpdateStatus(data: BulkUpdateStatusData) {
    return myAxios.post(`${BASE_URL}/bulk-update-status`, data);
  }

  /**
   * Bulk delete inputs with status filters
   */
  bulkDeleteInputs(data: BulkDeleteInputsData) {
    return myAxios.post(`${BASE_URL}/bulk-delete`, data);
  }

  /**
   * Bulk override the IsElective flag for inputs
   */
  bulkUpdateElective(data: { inputIds: number[]; isElective: 0 | 1 }) {
    return myAxios.post(`${BASE_URL}/bulk-update-elective`, data);
  }

  /**
   * Get statistics about inputs
   */
  getStats() {
    return myAxios.get(`${BASE_URL}/stats`);
  }

  /**
   * Reset a board - deletes all runs, slot templates, timetables, and resets input statuses
   */
  resetBoard(boardId: number) {
    return myAxios.post(`${BASE_URL}/reset-board`, { boardId });
  }

  // ============ Board Management ============

  /**
   * List all boards
   */
  listBoards() {
    return myAxios.get(BOARD_BASE_URL);
  }

  /**
   * Create a new board
   */
  createBoard(data: CreateBoardData) {
    return myAxios.post(BOARD_BASE_URL, data);
  }

  // ============ Board Resource Management ============

  /**
   * Get all resources for a board
   */
  getBoardResources(boardId: number) {
    return myAxios.get(`${BOARD_RESOURCE_BASE_URL}?boardId=${boardId}`);
  }

  /**
   * Get available classrooms and shifts
   */
  getAvailableResources(centreId?: number) {
    const params = centreId ? `?centreId=${centreId}` : '';
    return myAxios.get(`${BOARD_RESOURCE_BASE_URL}/available${params}`);
  }

  /**
   * Get resource summary for a board
   */
  getBoardResourceSummary(boardId: number) {
    return myAxios.get(`${BOARD_RESOURCE_BASE_URL}/summary?boardId=${boardId}`);
  }

  /**
   * Add a single resource to a board
   */
  addBoardResource(data: AddBoardResourceData) {
    return myAxios.post(BOARD_RESOURCE_BASE_URL, data);
  }

  /**
   * Bulk add resources to a board
   */
  bulkAddBoardResources(data: BulkAddBoardResourcesData) {
    return myAxios.post(`${BOARD_RESOURCE_BASE_URL}/bulk-add`, data);
  }

  /**
   * Remove a single resource from a board
   */
  removeBoardResource(resourceId: number) {
    return myAxios.delete(`${BOARD_RESOURCE_BASE_URL}/${resourceId}`);
  }

  /**
   * Bulk remove resources from a board
   */
  bulkRemoveBoardResources(resourceIds: number[]) {
    return myAxios.delete(`${BOARD_RESOURCE_BASE_URL}/bulk-remove`, { data: { resourceIds } });
  }

  // ============ Module Import ============

  /**
   * Preview modules available for import
   */
  previewModuleImport(startDate: string, endDate: string, instructorFilter?: string, programIds?: number[]) {
    let url = `${BASE_URL}/preview-import?startDate=${startDate}&endDate=${endDate}`;
    if (instructorFilter && instructorFilter !== 'all') {
      url += `&instructorFilter=${instructorFilter}`;
    }
    if (programIds && programIds.length > 0) {
      url += `&programIds=${programIds.join(',')}`;
    }
    return myAxios.get(url);
  }

  /**
   * Import modules from course_year_properties
   */
  importFromModules(data: {
    startDate: string;
    endDate: string;
    selectedCYPIds?: number[];
    modulesData?: { cypId: number; plannedCapacity: number }[];
    overwriteExisting?: boolean;
  }) {
    return myAxios.post(`${BASE_URL}/import-from-modules`, data);
  }

  /**
   * Update planned capacity for multiple modules
   */
  updateModuleCapacity(cypIds: number[], plannedCapacity: number) {
    return myAxios.patch(`${BASE_URL}/module-capacity`, { cypIds, plannedCapacity });
  }

  /**
   * Update instructors for a course year property
   */
  updateModuleInstructors(cypId: number, instructors: any[]) {
    return myAxios.patch(`${BASE_URL}/module-instructors`, { cypId, instructors });
  }

  /**
   * Get suggested instructors for a module based on capability, availability, and workload
   */
  getSuggestedInstructors(cypId: number) {
    return myAxios.get(`${BASE_URL}/suggest-instructors?cypId=${cypId}`);
  }

  /**
   * List all active academic programs
   */
  listPrograms() {
    return myAxios.get(`${Config.nodeSiteUrl}v1/academic/programs-list`);
  }
}

export default new TimeCraftInputService();
